import {
  adminApiFetch,
  getApiBase,
  getCurrentBrowserAuthHeaders,
} from "@/lib/api";
import type {
  ActivityLogFiltersResponse,
  ActivityLogListParams,
  ActivityLogListResponse,
  ActivityLogTrackPayload,
} from "@/types/activityLogs";

type TrackActivityOptions = {
  clientEmail?: string | null;
  clientId?: string | null;
};

const TRACKING_ENDPOINT = "/v1/activity-logs";

const buildQueryString = (params: ActivityLogListParams): string => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    searchParams.set(key, String(value));
  }

  const query = searchParams.toString();
  return query ? `?${query}` : "";
};

export async function listActivityLogs(
  params: ActivityLogListParams,
): Promise<ActivityLogListResponse> {
  const response = await adminApiFetch(
    `/v1/rights/activity-logs${buildQueryString(params)}`,
  );
  return response as ActivityLogListResponse;
}

export async function getActivityLogFilters(): Promise<ActivityLogFiltersResponse> {
  const response = await adminApiFetch("/v1/rights/activity-logs/filters");
  return response as ActivityLogFiltersResponse;
}

export async function trackActivityLog(
  payload: ActivityLogTrackPayload,
  options: TrackActivityOptions = {},
): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const base = getApiBase();
    const response = await fetch(`${base}${TRACKING_ENDPOINT}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getCurrentBrowserAuthHeaders({
          fallbackClientEmail: options.clientEmail,
          fallbackClientId: options.clientId,
        }),
      },
      body: JSON.stringify({
        source: "frontend",
        route_path: window.location.pathname,
        metadata: {},
        ...payload,
      }),
    });

    return response.ok;
  } catch {
    return false;
  }
}

export function trackPageView(
  payload: Omit<ActivityLogTrackPayload, "event_category" | "event_name"> & {
    event_name?: string;
  },
): Promise<boolean> {
  return trackActivityLog({
    ...payload,
    event_category: "page_view",
    event_name: payload.event_name ?? "page_view",
  });
}

export function trackExport(
  payload: {
    appArea?: string;
    clientId?: string | null;
    eventName?: string;
    fileName: string;
    metadata?: Record<string, unknown>;
    routePath?: string;
  },
  options: TrackActivityOptions = {},
): Promise<boolean> {
  return trackActivityLog(
    {
      app_area: payload.appArea,
      client_id: payload.clientId,
      event_category: "export",
      event_name: payload.eventName ?? "file_export",
      route_path: payload.routePath,
      metadata: {
        filename: payload.fileName,
        ...payload.metadata,
      },
    },
    options,
  );
}

